import React, { useEffect, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { queryTools } from "lib/utils"
import { useGenericQuery } from "lib/hooks"
import { useAppCtx, appStore } from "lib/state"
import { DropdownMenu, Modal, NewTemplateForm, Pagination, GridTable, GridTableTitle } from 'lib/components';

const defaultParams = { _page: 1, _perpage: 25, _sortby: "title", _sortdir: "asc" }
const defaultFilters = { id: "", client: "", title: "", tag: "" }
const testTags = [
  { label: "Test", value: "test" },
  { label: "Test2", value: "test2" },
  { label: "Test3", value: "test3" },
  { label: "Test4", value: "test4" }
]
const testClients = [
  { label: "Lifetime Well", value: "lifetimewell" },
  { label: "RestoMods", value: "restomods" },
  { label: "Scentbird", value: "scentbird" },
]

export default function DisputeList(){
  const navigate = useNavigate()

  const [ searchParams, setSearchParams ] = useSearchParams()
  const { AppStore }:any = appStore()
  const { update } = AppStore

  const [ filters, setFilters ] = useState<any>(defaultFilters)
  const [ queryParams, setQueryParams ] = useState<any>(defaultParams)
  const [ selectedTemplate, setSelectedTemplate ] = useState<any>(null)
  const [ selectedClient, setSelectedClient ] = useState<any>(null)
  const [ selectedClientTemplate, setSelectedClientTemplate ] = useState<any>(null)

  const { data:_Disputes } = useGenericQuery({ resource: "dispute", qk: ["disputes", queryParams._sortby, queryParams._sortdir], queryParams, enabled: true })

  const { data:_Clients } = useGenericQuery({ resource: "client", qk: ["clients"], queryParams: {_sortby: "name"}, enabled: true })
  
  const clientFilters = { client: "!null", rp:null, derived_from: selectedTemplate?._id || null }
  const clientRpFilters = { client: selectedClient?._id || null, rp:"!null", derived_from: selectedClientTemplate?._id || null }

  // const { data:_Disputes } = queryTools.resource.parseQueryForApi("dispute.findAll", {...queryParams, ...templateFilters}, { })
  const { data:_ClientDisputes } = queryTools.resource.parseQueryForApi("dispute.findAll", {...queryParams, ...clientFilters}, { enabled: !!selectedTemplate?._id })
  const { data:_ClientRpDisputes } = queryTools.resource.parseQueryForApi("dispute.findAll", {...queryParams, ...clientRpFilters}, { enabled: (!!selectedTemplate?._id && !!selectedClient?._id) })
  // const { data:_Template } = queryTools.resource.parseQueryForApi("dispute.findOne", { Id: 50 }, {})
  const removeFn = queryTools.resource.remove(["dispute.all"])
  
  async function handleTemplateDelete(_id:string){
    removeFn.mutate({ resource: "template", idObj: { key: "_id", value: _id} })
  }

  const [ openModal, setOpenModal ] = useState<string|null>(null)
  const [ formInit, setFormInit ] = useState<any>({})

  function prepQueryParams(){
    const _page = searchParams.get("_page") || defaultParams._page
    const _perpage = searchParams.get("_perpage") || defaultParams._perpage
    const _sortby = searchParams.get("_sortby") || queryParams._sortby || defaultParams._sortby
    const _sortdir = searchParams.get("_sortdir") || defaultParams._sortdir
    setQueryParams({ _page, _perpage, _sortby, _sortdir })
  }

  function createTemplate(action:string = "create", init:any = {}){
    console.log(action)
    setFormInit(init)
    setOpenModal(action)
  }

  // function deleteTemplate(_id:string){
  //   console.log("delete template", _id)
  // }

  function showConfirmDelete(_id:string){
    update("ui.toast", {
      id: Date.now(),
      visible: true,
      message: "Are you sure you want to delete this template?",
      type: "confirm",
      onConfirm: async() => await handleTemplateDelete(_id)
    })
  }
  
  function cloneDispute(_id:string){
    setFormInit({})
    const clone = _Disputes?.find( (item:any) => item._id === _id)
    const newCopy = {...clone, title: `Copy of ${clone.title}`}
    setFormInit(newCopy)
    setOpenModal("clone")
  }

  function handleTitleClick(_id:string){
    navigate(`/template/${_id}/build`)
  }

  // useEffect(() => {
  //   prepQueryParams()
  // },[searchParams.get("_page"), searchParams.get("_perpage"), searchParams.get("_sortby"), searchParams.get("_sortdir")])

  // useEffect(() => {
  //   console.log(_Template)
  // },[_Template])
  
  return (
    <div className="w-full h-full flex gap-[2rem]">

      <Modal  
        size={ openModal !== "variant" ? ["w-[20rem]", "h-[28rem]"] : ["w-[20rem]", "h-[32rem]"] } 
        title="Create New Template"  
        status={openModal ? "open" : "closed"}  
        onClose={() => setOpenModal(null)}
      >
        <NewTemplateForm 
          initData={{...formInit, action: openModal}} 
          setOpenModal={setOpenModal} 
        />
      </Modal>

      <div className="w-full flex flex-col">
        
        <GridTableTitle title={`Dispute Templates ${queryParams._sortby}`} addItemFn={createTemplate} />
        
        <div className="w-full h-8 my-2 border border-solid border-slate-700 flex justify-between items-center *:text-xs">
          <div className="w-9/12 h-full flex justify-start items-center gap-0">
            <div className="h-full pl-2 pr-4 flex items-center bg-teal-800">Filter By Tag:</div>
            <div className="flex gap-0 pl-1 h-full bg-slate-900">
              <DropdownMenu
                size="full" 
                theme="slate" 
                name="tag" 
                placeholder="Choose..."
                value={filters.tag || ""} 
                options={testTags}
                selectfn={(val:string) => setFilters({ ...filters, tag: val })}
                className="w-[7rem] border-y-0 border-l-0"
              />
              <div className="h-full pl-2 pr-4 flex items-center bg-teal-800">Filter By Client:</div>
              <DropdownMenu
                size="full" 
                theme="slate" 
                name="client" 
                placeholder="Choose..."
                value={filters.client || ""} 
                options={_Clients?.map( (item:any) => ({ label: item.name, value: item._id })) || []}
                selectfn={(val:string) => setFilters({ ...filters, client: val })}
                className="w-[8rem] border-y-0 border-l-0"
              />
            </div>
          </div>
          <div className="w-5/12 h-full flex justify-end items-center gap-2">
            <div className="h-full pl-2 pr-4 flex items-center bg-teal-800">Sort By:</div>
            <div className="flex pl-1 h-full bg-slate-900">
              <DropdownMenu
                size="full" 
                theme="slate" 
                name="sortby" 
                placeholder="Choose..."
                value={queryParams._sortby || ""} 
                options={["Id", "title"].map( (item:string) => ({ label: item, value: item }))}
                selectfn={(val:string) => {
                  console.log("sortby", val)
                  setQueryParams( (prev:any) => ({ ...prev, _sortby: val }) )
                }}
                className="w-[7rem] border-y-0 border-l-0"
              />
            <div className="h-full pl-2 pr-4 flex items-center bg-teal-800">Sort Dir:</div>
            <div className="flex pl-1 h-full bg-slate-900">
              <DropdownMenu
                size="full" 
                theme="slate" 
                name="sortdir" 
                placeholder="Choose..."
                value={queryParams._sortdir || ""} 
                options={["asc", "desc"].map( (item:string) => ({ label: item, value: item }))}
                selectfn={(val:string) => {
                  setQueryParams( (prev:any) => ({ ...prev, _sortdir: val }) )
                }}
                className="w-[7rem] border-y-0 border-l-0"
              />
            </div>
          </div>
        </div>

      </div>
                

        <GridTable 
          dataset={_Disputes || []}
          isSelectedFn={(item:any) => item._id === selectedTemplate?._id}
          rowClickFn={(row:any) => navigate(`/dispute/${row._id}/setup`)}
          className="px-4"
          columns={[
            { name: "id", label: "Id", dataprop: "Id", className: "col-span-2" },
            { name: "title", label: "Title", dataprop: "title", className: "col-span-7" },
            { name: "design", label: "Design", dataprop: "design", className: "col-span-4" },
            { name: "layout", label: "Layout", dataprop: "layout", className: "col-span-4" },
            { name: "tags", label: "Tags", dataprop: "tags", className: "col-span-5" },
            { name: "[actions]", label: "--", className: "col-span-2" }
          ]}
          actions={[
            { name: "search", label: "Search", clickFn: (item:any) => setSelectedTemplate(item) },
            { name: "clone", label: "Clone", clickFn: (item:any) => cloneDispute(item._id) },
            { name: "delete", label: "Delete", clickFn: (item:any) => showConfirmDelete(item._id) }
          ]}
        />
    </div>

    </div>
  )
}